// Drawing primitives for the Square's illustrated look: ink-outlined,
// softly shaded shapes on a meadow, the way a storybook map is painted.
// Everything is deterministic from a seed so every visitor sees the same map.

export function rng(seed) {
    let s = (Math.abs(Math.floor(seed)) % 2147483646) + 1
    return () => {
        s = (s * 16807) % 2147483647
        return (s - 1) / 2147483646
    }
}

// Smooth 2D value noise in [0, 1], for washes and path edges.
export function noise2(seed) {
    const perm = new Uint8Array(512)
    const r = rng(seed)
    const p = Array.from({ length: 256 }, (_, i) => i)
    for (let i = 255; i > 0; i--) { const j = Math.floor(r() * (i + 1)); [p[i], p[j]] = [p[j], p[i]] }
    for (let i = 0; i < 512; i++) perm[i] = p[i & 255]
    const values = Array.from({ length: 256 }, () => r())
    const fade = (t) => t * t * (3 - 2 * t)
    return (x, y) => {
        const xi = Math.floor(x), yi = Math.floor(y), xf = x - xi, yf = y - yi
        const v = (i, j) => values[perm[(perm[(xi + i) & 255] + yi + j) & 255]]
        const u = fade(xf), w = fade(yf)
        const a = v(0, 0) + (v(1, 0) - v(0, 0)) * u
        const b = v(0, 1) + (v(1, 1) - v(0, 1)) * u
        return a + (b - a) * w
    }
}

export function withAlpha(hex, alpha) {
    const h = hex.replace("#", "")
    const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16)
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`
}

export function mix(a, b, t) {
    const pa = parseInt(a.slice(1), 16), pb = parseInt(b.slice(1), 16)
    const ch = (n, s) => (n >> s) & 255
    const c = (s) => Math.round(ch(pa, s) + (ch(pb, s) - ch(pa, s)) * t)
    return `#${((1 << 24) + (c(16) << 16) + (c(8) << 8) + c(0)).toString(16).slice(1)}`
}

// A soft, feathered shadow on the ground.
export function softShadow(ctx, x, y, rx, ry, alpha = 0.22, color = "20,30,25") {
    ctx.save()
    ctx.translate(x, y)
    ctx.scale(1, ry / rx)
    const g = ctx.createRadialGradient(0, 0, rx * 0.15, 0, 0, rx)
    g.addColorStop(0, `rgba(${color},${alpha})`)
    g.addColorStop(0.6, `rgba(${color},${alpha * 0.55})`)
    g.addColorStop(1, `rgba(${color},0)`)
    ctx.fillStyle = g
    ctx.beginPath(); ctx.arc(0, 0, rx, 0, Math.PI * 2); ctx.fill()
    ctx.restore()
}

// A watercolor-ish wash: a big soft blotch of color.
export function wash(ctx, x, y, r, color, alpha) {
    const g = ctx.createRadialGradient(x, y, 0, x, y, r)
    g.addColorStop(0, withAlpha(color, alpha))
    g.addColorStop(0.7, withAlpha(color, alpha * 0.45))
    g.addColorStop(1, withAlpha(color, 0))
    ctx.fillStyle = g
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
}

// A closed blob whose edge wobbles, as a path (fill/stroke it yourself).
export function blobPath(ctx, x, y, rx, ry, seed, jitter = 0.1, points = 26) {
    const r = rng(seed)
    const offsets = Array.from({ length: points }, () => 1 + (r() - 0.5) * jitter * 2)
    ctx.beginPath()
    for (let i = 0; i <= points; i++) {
        const k0 = offsets[i % points], k1 = offsets[(i + 1) % points]
        const a0 = (i / points) * Math.PI * 2, a1 = ((i + 1) / points) * Math.PI * 2
        const x0 = x + Math.cos(a0) * rx * k0, y0 = y + Math.sin(a0) * ry * k0
        const x1 = x + Math.cos(a1) * rx * k1, y1 = y + Math.sin(a1) * ry * k1
        if (i === 0) ctx.moveTo((x0 + x1) / 2, (y0 + y1) / 2)
        else ctx.quadraticCurveTo(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2)
    }
    ctx.closePath()
}

// A set of circles drawn as one inked silhouette: the union's outline in
// ink, then the fill on top so inner seams disappear.
export function inkedCircles(ctx, circles, fill, ink, line = 1.8) {
    ctx.fillStyle = ink
    circles.forEach(([x, y, r]) => { ctx.beginPath(); ctx.arc(x, y, r + line, 0, Math.PI * 2); ctx.fill() })
    ctx.fillStyle = fill
    circles.forEach(([x, y, r]) => { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill() })
}

export function clipCircles(ctx, circles) {
    ctx.beginPath()
    circles.forEach(([x, y, r]) => { ctx.moveTo(x + r, y); ctx.arc(x, y, r, 0, Math.PI * 2) })
    ctx.clip()
}

// Pencil hatching inside the current clip: parallel, slightly wobbly strokes.
export function hatch(ctx, bounds, color, { seed = 1, angle = -0.85, gap = 3.2, alpha = 0.3, width = 1 } = {}) {
    const r = rng(seed)
    const [bx, by, bw, bh] = bounds
    const cx = bx + bw / 2, cy = by + bh / 2, reach = Math.hypot(bw, bh) / 2 + 2
    const cos = Math.cos(angle), sin = Math.sin(angle)
    ctx.save()
    ctx.strokeStyle = color
    ctx.lineWidth = width
    ctx.globalAlpha *= alpha
    ctx.beginPath()
    for (let d = -reach; d <= reach; d += gap * (0.8 + r() * 0.4)) {
        const ox = cx - sin * d, oy = cy + cos * d
        ctx.moveTo(ox - cos * reach, oy - sin * reach)
        ctx.lineTo(ox + cos * reach, oy + sin * reach)
    }
    ctx.stroke()
    ctx.restore()
}

// A polygon filled flat, optionally shaded with a vertical gradient, and
// outlined in ink.
export function inkPoly(ctx, pts, fill, ink, { line = 1.6, shade = null } = {}) {
    ctx.beginPath()
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)))
    ctx.closePath()
    if (shade) {
        const ys = pts.map((p) => p[1]), top = Math.min(...ys), bottom = Math.max(...ys)
        const g = ctx.createLinearGradient(0, top, 0, bottom)
        g.addColorStop(0, shade[0]); g.addColorStop(1, shade[1])
        ctx.fillStyle = g
    } else {
        ctx.fillStyle = fill
    }
    ctx.fill()
    if (ink) {
        ctx.strokeStyle = ink
        ctx.lineWidth = line
        ctx.lineJoin = "round"
        ctx.stroke()
    }
}

export function inkRect(ctx, x, y, w, h, fill, ink, opts) {
    inkPoly(ctx, [[x, y], [x + w, y], [x + w, y + h], [x, y + h]], fill, ink, opts)
}

export function inkEllipse(ctx, x, y, rx, ry, fill, ink, line = 1.6) {
    ctx.beginPath(); ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2)
    if (fill) { ctx.fillStyle = fill; ctx.fill() }
    if (ink) { ctx.strokeStyle = ink; ctx.lineWidth = line; ctx.stroke() }
}

export function line(ctx, pts, color, width = 1.4, alpha = 1) {
    ctx.save()
    ctx.globalAlpha *= alpha
    ctx.strokeStyle = color
    ctx.lineWidth = width
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
    ctx.beginPath()
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)))
    ctx.stroke()
    ctx.restore()
}

export function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.arcTo(x + w, y, x + w, y + h, r)
    ctx.arcTo(x + w, y + h, x, y + h, r)
    ctx.arcTo(x, y + h, x, y, r)
    ctx.arcTo(x, y, x + w, y, r)
    ctx.closePath()
}

// Points along a quadratic-smoothed polyline, `step` units apart, each with
// its direction — used to lay paths and fences.
export function samplePath(pts, step = 6) {
    const out = []
    const segs = []
    let prev = pts[0]
    for (let i = 1; i < pts.length - 1; i++) {
        const mid = [(pts[i][0] + pts[i + 1][0]) / 2, (pts[i][1] + pts[i + 1][1]) / 2]
        segs.push([prev, pts[i], mid])
        prev = mid
    }
    segs.push([prev, pts[pts.length - 1], pts[pts.length - 1]])
    segs.forEach(([a, c, b]) => {
        const approx = Math.hypot(c[0] - a[0], c[1] - a[1]) + Math.hypot(b[0] - c[0], b[1] - c[1])
        const n = Math.max(2, Math.ceil(approx / step))
        for (let i = 0; i < n; i++) {
            const t = i / n, u = 1 - t
            const x = u * u * a[0] + 2 * u * t * c[0] + t * t * b[0]
            const y = u * u * a[1] + 2 * u * t * c[1] + t * t * b[1]
            const dx = 2 * u * (c[0] - a[0]) + 2 * t * (b[0] - c[0])
            const dy = 2 * u * (c[1] - a[1]) + 2 * t * (b[1] - c[1])
            const len = Math.hypot(dx, dy) || 1
            out.push({ x, y, nx: -dy / len, ny: dx / len })
        }
    })
    const last = pts[pts.length - 1]
    out.push({ x: last[0], y: last[1], nx: out.at(-1)?.nx || 0, ny: out.at(-1)?.ny || 1 })
    return out
}
